/*
 * One asynchronous action of a surface — create the channel, add the people, hand the account over
 * — with the two pieces of state every one of them used to keep by hand: whether it is running, and
 * the refusal it ended with.
 *
 * The refusal is kept as a **locale key**, not as a sentence. The server never sends prose
 * (protocol.md §1.1), so the key is the durable thing; a component that stored the translated text
 * would have to re-run the translation itself when the copy or the language changes, and ten copies
 * of that rule is what this hook replaces. `FormAlert` turns the key into the sentence.
 *
 * `run` never runs twice at once: the guard is a ref rather than the `busy` flag, because two
 * clicks in one tick both read the same stale render.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { textOfKey } from "../core/i18n/index.ts"
import { WireError } from "../core/net/api.ts"

export interface AsyncAction<A extends unknown[]> {
	run: (...args: A) => Promise<void>
	/** True while the action is in flight; the caller disables its control with it. */
	busy: boolean
	/** The locale key of the refusal, or null while nothing has failed. */
	error: string | null
	/** Clears the refusal — for a surface that reopens its form and starts again. */
	reset: () => void
}

/**
 * The key that names a failure: the server's own, else the generic one for its wire code, else the
 * one every unknown failure ends at. The two-step fallback is what keeps an older client from
 * showing a raw key at a person.
 */
function refusalKey(err: unknown): string {
	if (!(err instanceof WireError)) return "error.unknown"
	if (textOfKey(err.key) !== "") return err.key
	if (textOfKey(`error.${err.code}`) !== "") return `error.${err.code}`
	return "error.unknown"
}

export function useAsyncAction<A extends unknown[]>(
	fn: (...args: A) => Promise<void>,
): AsyncAction<A> {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// The action is written inline at the call site, so it is a different function on every render.
	// Keeping the newest one in a ref is what gives `run` a single identity for the life of the
	// component — an effect may then depend on it, which is how a dialog loads its list on open.
	const latest = useRef(fn)
	useEffect(() => {
		latest.current = fn
	})

	const running = useRef(false)
	const run = useCallback(async (...args: A) => {
		if (running.current) return
		running.current = true
		setBusy(true)
		setError(null)
		try {
			await latest.current(...args)
		} catch (err) {
			setError(refusalKey(err))
		} finally {
			running.current = false
			setBusy(false)
		}
	}, [])

	const reset = useCallback(() => setError(null), [])

	return { run, busy, error, reset }
}
